import React, { useEffect, useState }  from "react";
import styled from "styled-components";
import { useAuth} from "../../AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NavigationItem } from "./NavigationItem";
import { AutoFixHigh, AutoStories, ChevronLeft, ChevronRight, Dashboard, Schedule, Campaign, People, Settings, MeetingRoom, Logout, InterpreterMode, Synagogue, DarkMode, ExpandMore, ExpandLess, CreditCard } from "@mui/icons-material";
import { brand, gray, typography, shape, shadows } from "../../themePrimitives"; // Import du thème
import { Drawer, Box, Typography, List, ListItem, Chip } from "@mui/material";
import SitemarkIcon from '../SitemarkIcon';
import PaywallModal from "../billing/PaywallModal";
import useAuthenticatedFetch from "../../hooks/useAuthenticatedFetch";

const NavigationMenu = ({ onTabChange, isMobileOpen, toggleMobileMenu, isDirty}) => {
  const { t } = useTranslation(); // Traduction
  const { logout, isTokenExpired, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fetchWithAuth = useAuthenticatedFetch();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedPath, setSelectedPath] = useState(location.pathname); // État pour suivre le chemin sélectionné
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false); // État pour le sous-menu Settings
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState("");
  const [proActive, setProActive] = useState(null);
  const shouldShowProBadge = (requiresPro) => Boolean(requiresPro && proActive === false);

  const toggleMenu = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleSettingsMenu = () => {
    setSettingsMenuOpen(!settingsMenuOpen);
  };

  const navigationItems = [
    {
      icon: <Dashboard />,
      label: t("navigation.templateSelection"),
      action: () => onTabChange(0),
      path: "/AdminDashboard",
      requiresPro: false,
    },
    {
      icon: <CreditCard />,
      label: t("navigation.billing"),
      action: () => onTabChange(11),
      path: "/AdminDashboard/billing",
      requiresPro: true,
    },
    {
      icon: <AutoFixHigh />,
      label: t("navigation.layoutBuilder"),
      action: () => onTabChange(10),
      path: "/AdminDashboard/layoutBuilder",
      requiresPro: true,
    },
    {
      icon: <Schedule />,
      label: t("navigation.scheduleEditing"),
      action: () => onTabChange(1),
      path: "/AdminDashboard/schedule",
      requiresPro: false,
    },
    {
      icon: <Synagogue />,
      label: t("navigation.chaguimEditing"),
      action: () => onTabChange(2),
      path: "/AdminDashboard/chaguim",
      requiresPro: false,
    },
    {
      icon: <Campaign />,
      label: t("navigation.adManagement"),
      action: () => onTabChange(3),
      path: "/AdminDashboard/adManagement",
      requiresPro: false,
    },
    {
      icon: <People />,
      label: t("navigation.sponsorManagement"),
      action: () => onTabChange(4),
      path: "/AdminDashboard/sponsorManagement",
      requiresPro: true,
    },
    {
      icon: <AutoStories />, // Icône similaire pour l'instant, tu peux la changer si besoin
      label: t("navigation.courseManagement"),
      action: () => onTabChange(5),
      path: "/AdminDashboard/courseManagement",
      requiresPro: true,
    },
  ];

  const settingsSubItems = [
    {
      icon: <Settings />,
      label: t("navigation.general"),
      action: () => onTabChange(8),
      path: "/AdminDashboard/settings/general",
      requiresPro: false,
    },
    {
      icon: <DarkMode />,
      label: t("navigation.zmanimEditing"),
      action: () => onTabChange(9),
      path: "/AdminDashboard/settings/zmanim",
      requiresPro: true,
    },
    {
      icon: <InterpreterMode />,
      label: t("navigation.ravManagement"),
      action: () => onTabChange(6),
      path: "/AdminDashboard/settings/ravManagement",
      requiresPro: true,
    },
    {
      icon: <MeetingRoom />,
      label: t("navigation.roomsManagement"),
      action: () => onTabChange(7),
      path: "/AdminDashboard/settings/roomsManagement",
      requiresPro: true,
    },
  ];

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const ensureEntitlements = async () => {
    if (!user?.communityId) return { proActive: false };
    if (typeof proActive === "boolean") return { proActive };
    const res = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/billing/status/${user.communityId}`);
    const json = await res.json().catch(() => null);
    const ok = res.ok && Boolean(json?.proActive);
    setProActive(ok);
    return { proActive: ok };
  };

  useEffect(() => {
    let cancelled = false;

    const loadEntitlements = async () => {
      if (!user?.communityId) {
        setProActive(false);
        return;
      }

      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/billing/status/${user.communityId}`);
        const json = await res.json().catch(() => null);
        if (!cancelled) {
          setProActive(res.ok && Boolean(json?.proActive));
        }
      } catch {
        if (!cancelled) {
          setProActive(false);
        }
      }
    };

    setProActive(null);
    loadEntitlements();

    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth, user?.communityId]);

  const handleNavigationClick = (path, action) => {
    if (isTokenExpired()) {
      alert("Your session has expired. Please log in again.");
      logout();
      navigate("/");
      return;
    }
    const isFreeAllowed =
      path === "/AdminDashboard" ||
      path === "/AdminDashboard/schedule" ||
      path === "/AdminDashboard/chaguim" ||
      path === "/AdminDashboard/adManagement" ||
      path === "/AdminDashboard/settings/general";

    const go = async () => {
      if (!isFreeAllowed) {
        try {
          const ent = await ensureEntitlements();
          if (!ent.proActive) {
            setPaywallFeature(path === "/AdminDashboard/layoutBuilder" ? t("navigation.layoutBuilder") : t("billing.paywall.defaultFeature"));
            setPaywallOpen(true);
            return;
          }
        } catch {
          // If entitlements endpoint fails, default to showing paywall for safety.
          setPaywallFeature(t("billing.paywall.defaultFeature"));
          setPaywallOpen(true);
          return;
        }
      }
      setSelectedPath(path);
      action();
    };

    go();
  };

  const handleSettingsClick = () => {
    if (isTokenExpired()) {
      alert("Your session has expired. Please log in again.");
      logout();
      navigate("/");
      return;
    }
    toggleSettingsMenu();
  };

  return (
    <>
    <PaywallModal
      open={paywallOpen}
      onClose={() => setPaywallOpen(false)}
      communityId={user?.communityId}
      featureLabel={paywallFeature}
    />
    {/* Drawer for Mobile */}
    <Drawer
      anchor="left"
      open={isMobileOpen}
      onClose={toggleMobileMenu}
      sx={{
        "& .MuiDrawer-paper": {
          width: "256px",
        },
      }}
    >
      <Box role="navigation" sx={{ width: 256 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
         <SitemarkIcon/>
        </Box>
        <List>
          {navigationItems.map((item) => (
            <ListItem
              button
              key={item.label}
              onClick={() => { handleNavigationClick(item.path, item.action); toggleMobileMenu(); }}
              sx={{
                opacity: item.requiresPro && proActive === false ? 0.55 : 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {item.icon}
              <Typography sx={{ ml: 0.5, flexGrow: 1 }}>{item.label}</Typography>
              {shouldShowProBadge(item.requiresPro) ? (
                <Chip label="PRO" size="small" variant="outlined" sx={{ height: 20, fontWeight: 800 }} />
              ) : null}
            </ListItem>
          ))}
          
          {/* Settings section with submenu */}
          <ListItem
            button
            onClick={handleSettingsClick}
            sx={{
              opacity: proActive === false ? 0.55 : 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Settings />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{t("navigation.settings")}</Typography>
            {shouldShowProBadge(true) ? (
              <Chip label="PRO" size="small" variant="outlined" sx={{ height: 20, fontWeight: 800, mr: 1 }} />
            ) : null}
            {settingsMenuOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          
          {settingsMenuOpen && settingsSubItems.map((item) => (
            <ListItem 
              button 
              key={item.label} 
              onClick={() => { handleNavigationClick(item.path, item.action); toggleMobileMenu(); }}
              sx={{
                pl: 4,
                opacity: item.requiresPro && proActive === false ? 0.55 : 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {item.icon}
              <Typography sx={{ ml: 1, flexGrow: 1 }}>{item.label}</Typography>
              {shouldShowProBadge(item.requiresPro) ? (
                <Chip label="PRO" size="small" variant="outlined" sx={{ height: 20, fontWeight: 800 }} />
              ) : null}
            </ListItem>
          ))}
          
          <ListItem button onClick={handleSignOut}>
            <Logout />
            <Typography sx={{ ml: 1 }}>{t("navigation.logout")}</Typography>
          </ListItem>
        </List>
      </Box>
    </Drawer>

    <StyledNav isCollapsed={isCollapsed} role="navigation" aria-label={t("navigation.ariaLabel")}>
      <ToggleButton onClick={toggleMenu} isCollapsed={isCollapsed}>
        {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
      </ToggleButton>
      
      {/* Navigation items principaux */}
      {navigationItems.map((item) => (
        <NavigationItem
          key={item.label}
          icon={item.icon}
          label={item.label}
          isSelected={selectedPath === item.path}
          tabIndex={0}
          onClick={() => handleNavigationClick(item.path, item.action)}
          isCollapsed={isCollapsed}
          isLocked={item.requiresPro && proActive === false}
          showProBadge={shouldShowProBadge(item.requiresPro)}
        />
      ))}
      
      {/* Settings avec sous-menu */}
      <NavigationItem
        icon={<Settings />}
        label={t("navigation.settings")}
        isSelected={settingsMenuOpen}
        onClick={handleSettingsClick}
        isCollapsed={isCollapsed}
        hasSubmenu={true}
        submenuOpen={settingsMenuOpen}
        isLocked={proActive === false}
        showProBadge={false}
      />
      
      {/* Sous-menu Settings */}
      {!isCollapsed && settingsMenuOpen && (
        <SubMenuContainer isRTL={t('direction') === 'rtl'}>
          {settingsSubItems.map((item) => (
            <SubNavigationItem
              key={item.label}
              icon={item.icon}
              label={item.label}
              isSelected={selectedPath === item.path}
              onClick={() => handleNavigationClick(item.path, item.action)}
              isLocked={item.requiresPro && proActive === false}
              showProBadge={shouldShowProBadge(item.requiresPro)}
            />
          ))}
        </SubMenuContainer>
      )}
      
      <NavigationItem
        icon={<Logout />}
        label={t("navigation.logout")}
        onClick={handleSignOut}
        isCollapsed={isCollapsed}
      />
    </StyledNav>
    </>
  );
};

// Composant pour les éléments de sous-menu
const SubNavigationItem = ({ icon, label, isSelected, onClick, isLocked, showProBadge }) => {
  return (
    <SubNavItemWrapper
      isSelected={isSelected}
      onClick={onClick}
      isLocked={isLocked}
    >
      <SubNavIconWrapper>{icon}</SubNavIconWrapper>
      <SubNavLabel>{label}</SubNavLabel>
      {showProBadge ? <SubProBadge>PRO</SubProBadge> : null}
    </SubNavItemWrapper>
  );
};

const ToggleButton = styled.div`
  position: absolute;
  top: 16px;
  right: ${(props) => (props.isCollapsed ? "-10px" : "-10px")};
  background-color: ${brand[100]};
  color: ${brand[900]};
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10; /* S'assurer qu'il est visible */
  box-shadow: ${shadows[1]};

  &:hover {
    background-color: ${brand[500]};
  }

  & > svg {
    font-size: 20px;
  }
`;

const SubMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${(props) => (props.isRTL ? '0' : '16px')};
  margin-right: ${(props) => (props.isRTL ? '16px' : '0')};
  border-left: ${(props) => (props.isRTL ? 'none' : '2px solid ' + gray[200])};
  border-right: ${(props) => (props.isRTL ? '2px solid ' + gray[200] : 'none')};
  padding-left: ${(props) => (props.isRTL ? '0' : '24px')};
  padding-right: ${(props) => (props.isRTL ? '24px' : '0')};
`;

const SubNavItemWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-top: 4px;
  background-color: ${(props) => (props.isSelected ? "rgba(0, 0, 0, 0.04)" : "inherit")};
  cursor: pointer;
  padding: 6px 12px;
  gap: 12px;
  align-items: center;
  border-radius: 4px;
  opacity: ${(props) => (props.isLocked ? 0.55 : 1)};

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const SubNavIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;

  & > svg {
    width: 100%;
    height: 100%;
    color: inherit;
  }
`;

const SubNavLabel = styled.span`
  font-size: 0.875rem;
  color: ${gray[700]};
`;

const SubProBadge = styled.span`
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.6px;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.16);
  color: rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.04);
`;

const StyledNav = styled.nav`
  border-radius:${shape.borderRadius}px;
  background-color:${gray[50]};
  display: flex;
  width: ${(props) => (props.isCollapsed ? '60px' : '256px')}; /* Dynamique */
  flex-direction: column;
  color: ${gray[800]};
  white-space: nowrap;
  letter-spacing: 0.1px;
  justify-content: start;
  padding: 24px 0;
  font: ${typography.body1.fontWeight} ${typography.body1.fontSize} ${typography.fontFamily};
  transition: width 0.3s ease-in-out;
  overflow: visible;
  position: relative;
  @media (max-width: 900px) {
    display: none; /* Masquer le menu sur mobile */
  }
`;

export default NavigationMenu;
